/**
 * @fileoverview EditableDetailField Component
 *
 * Universal component for displaying and editing entity fields on detail pages.
 * Supports text inputs, textareas, and picker modals with a consistent UI.
 *
 * Key features:
 * - Hover-to-reveal edit button
 * - Inline text editing with save/cancel
 * - Picker modal integration
 * - Loading states
 * - Chip/badge display for non-empty values
 *
 * @module Components/EditableDetailField
 */

import React from "react";
import { PencilSimple, Check, X } from "@phosphor-icons/react";
import { Button } from "components/ui/button";
import { Input } from "components/ui/input";
import { Textarea } from "components/ui/textarea";
import { Loader2 } from "lucide-react";
import UniversalPickerModal from "../UniversalPickerModal/UniversalPickerModal";
import { EditableDetailFieldProps } from "./types";
import { useFieldEditing } from "./hooks/useFieldEditing";

export const EditableDetailField: React.FC<EditableDetailFieldProps> = ({
  config,
  entity,
  onUpdate,
  apiEndpoint,
  compact = false,
  disabled = false,
}) => {
  const {
    isEditing,
    isPickerOpen,
    textValue,
    isUpdating,
    setTextValue,
    startEditing,
    cancelEditing,
    saveTextValue,
    handlePickerConfirm,
    setIsPickerOpen,
  } = useFieldEditing({ config, entity, onUpdate, apiEndpoint });

  console.log(
    "EditableDetailField - field:",
    config.id,
    "has customDisplayRenderer:",
    !!config.customDisplayRenderer,
  );

  const displayValue = config.displayValue(entity);
  console.log(
    "EditableDetailField - field:",
    config.id,
    "displayValue:",
    displayValue,
  );

  // If customDisplayRenderer is present, don't use displayValue for hasValue check
  const hasValue = config.customDisplayRenderer
    ? true // Let customDisplayRenderer handle empty state
    : displayValue && displayValue !== "-" && displayValue !== "Niet ingesteld";
  const Icon = config.icon;

  /** Render text editing mode */
  const renderTextEditing = () => (
    <div className="flex flex-1 flex-col gap-2">
      {config.type === "textarea" ? (
        <Textarea
          value={textValue}
          onChange={(e) => setTextValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && e.ctrlKey) saveTextValue();
            if (e.key === "Escape") cancelEditing();
          }}
          placeholder={
            config.placeholder || `Voer ${config.label.toLowerCase()} in`
          }
          className={`border-moss-primary focus:ring-moss-primary flex-1 ${compact ? "min-h-[100px] text-sm" : "min-h-[200px] text-base"}`}
          autoFocus
          disabled={isUpdating}
        />
      ) : (
        <div className="flex items-center gap-2">
          <Input
            value={textValue}
            onChange={(e) => setTextValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") saveTextValue();
              if (e.key === "Escape") cancelEditing();
            }}
            placeholder={
              config.placeholder || `Voer ${config.label.toLowerCase()} in`
            }
            className={`border-moss-primary focus:ring-moss-primary flex-1 ${compact ? "text-sm" : "text-base font-semibold"}`}
            autoFocus
            disabled={isUpdating}
          />
        </div>
      )}
      <div className="flex justify-end gap-2">
        <Button
          size={compact ? "sm" : "default"}
          onClick={saveTextValue}
          disabled={isUpdating}
          className="bg-moss-primary hover:bg-moss-primary/90 text-white"
        >
          {isUpdating ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <Check
              size={compact ? 14 : 16}
              className={config.type === "textarea" ? "mr-2" : ""}
            />
          )}
          {config.type === "textarea" && !isUpdating && " Opslaan"}
        </Button>
        <Button
          size={compact ? "sm" : "default"}
          variant="outline"
          onClick={cancelEditing}
          disabled={isUpdating}
        >
          <X
            size={compact ? 14 : 16}
            className={config.type === "textarea" ? "mr-2" : ""}
          />
          {config.type === "textarea" && " Annuleren"}
        </Button>
      </div>
    </div>
  );

  /** Render display mode for textarea */
  const renderTextareaDisplay = () => (
    <div className="flex h-full w-full flex-col gap-2">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {Icon && (
            <Icon size={16} className="text-neutral-secondary-mid shrink-0" />
          )}
          <span className="text-neutral-primary text-sm font-medium">
            {config.label}
          </span>
        </div>
        {!disabled && config.type !== "readonly" && (
          <Button
            size="sm"
            variant="ghost"
            onClick={startEditing}
            disabled={isUpdating}
            className="bg-moss-primary/10 text-moss-primary hover:bg-moss-primary/20 h-8 w-8 shrink-0 p-0"
          >
            {isUpdating ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <PencilSimple size={14} weight="bold" />
            )}
          </Button>
        )}
      </div>
      <div className="min-h-0 flex-1 overflow-auto">
        {hasValue ? (
          <div className="text-neutral-primary bg-neutral-primary/5 border-neutral-primary/10 rounded-md border p-3 text-sm whitespace-pre-wrap">
            {displayValue}
          </div>
        ) : (
          <div className="text-neutral-secondary-mid bg-neutral-primary/5 border-neutral-primary/10 rounded-md border p-3 text-sm italic">
            Geen notitie
          </div>
        )}
      </div>
    </div>
  );

  /** Render inline display mode with visible edit button */
  const renderInlineDisplay = () => (
    <div className="flex w-full items-center justify-between gap-3">
      <div className="flex min-w-0 flex-1 items-center gap-2">
        {Icon && (
          <Icon size={16} className="text-neutral-secondary-mid shrink-0" />
        )}
        <span className="text-neutral-primary shrink-0 text-sm font-medium">
          {config.label}
        </span>
        <div className="flex min-w-0 flex-1 items-center justify-end">
          {config.customDisplayRenderer ? (
            // Use custom renderer if provided (for complex UI like avatars)
            config.customDisplayRenderer(entity)
          ) : hasValue ? (
            <span className="text-neutral-primary truncate text-sm font-medium">
              {displayValue}
            </span>
          ) : (
            <span className="text-neutral-secondary-mid text-sm italic">
              Niet ingesteld
            </span>
          )}
        </div>
      </div>

      {!disabled && config.type !== "readonly" && (
        <Button
          size="sm"
          variant="ghost"
          onClick={startEditing}
          disabled={isUpdating}
          className="bg-moss-primary/10 text-moss-primary hover:bg-moss-primary/20 h-8 w-8 shrink-0 p-0"
        >
          {isUpdating ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <PencilSimple size={14} weight="bold" />
          )}
        </Button>
      )}
    </div>
  );

  return (
    <div
      className={`${config.type === "textarea" ? "flex h-full flex-col" : "flex items-center"} ${config.className || ""} ${compact ? "py-1.5" : "py-2.5"}`}
    >
      {isEditing
        ? renderTextEditing()
        : config.type === "textarea"
          ? renderTextareaDisplay()
          : renderInlineDisplay()}

      {/* Picker Modal */}
      {((config.type === "picker" && config.pickerMode) ||
        config.type === "date") && (
        <UniversalPickerModal
          isOpen={isPickerOpen}
          onClose={() => setIsPickerOpen(false)}
          // Date fields share the helpdesk's `document-date` picker UX so
          // there's a single, consistent calendar dialog across the app.
          mode={
            config.type === "date" ? "document-date" : config.pickerMode!
          }
          title={`Wijzig ${config.label.toLowerCase()}`}
          onConfirm={handlePickerConfirm}
          config={{
            defaultValue: config.extractValue(entity),
            contextValue: config.extractContextValue?.(entity),
            allowRemoval: true,
            ...config.pickerConfig,
            ...config.customPickerConfig,
          }}
        />
      )}
    </div>
  );
};
